import { Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ButtonV2, PopoverHover } from '@labrav/react-components'
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { showAddUserForm } from '../../modules/addUserForm/actions'
import type { AddNewUsersState } from '../../modules/addUserForm/reducer'
import { ssoInviteDisableMessage } from '../../utils/messagesContants'
import { usePartner } from '../../utils/hooks/usePartner'
import { useTenantPartnerId, useUserType } from '../../utils/hooks'
import { useStyles } from './TeamMembersV2.styles'
import { AddNewUserModal } from '../AddNewUserModal'

export type InviteModalProps = {
  openForm: boolean
  errors: AddNewUsersState['errors']
}

type TeamMembersRootState = {
  addNewUsers?: {
    metadata?: AddNewUsersState
  }
}

export const TeamMembersV2 = () => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const queryParams = new URLSearchParams(location.search)
  const firstTimeValue = queryParams.get('newFirst') === 'true'
  const [open, setOpen] = useState<boolean>(firstTimeValue || false)
  const [firstTime, setFirstTime] = useState<boolean>(firstTimeValue || false)
  const [openDisabledMessage, setOpenDisabledMessage] = useState<boolean>(false)

  const { openForm, errors } = useSelector((state: TeamMembersRootState) =>
    state.addNewUsers?.metadata
      ? state.addNewUsers.metadata
      : { openForm: false, errors: [], loading: false }
  )

  const handleOpen = () => {
    dispatch(showAddUserForm())
  }

  const partnerId = useTenantPartnerId()
  const partnerUser = useUserType()
  const { partnerData } = usePartner(partnerId || '', partnerUser)
  const shouldInviteButtonDisabled = partnerData?.authProtocol === 'EXTERNAL'

  const removeQueryParam = () => {
    queryParams.delete('newFirst')
    navigate({ search: queryParams.toString() }, { replace: true })
  }

  const handleAction = async () => {
    await setOpen(false)
    await setFirstTime(false)
    if (queryParams?.has('newFirst')) {
      removeQueryParam()
    }

    setTimeout(() => {
      handleOpen()
    }, 300)
  }

  const inviteLater = async () => {
    if (queryParams?.has('newFirst')) {
      removeQueryParam()
    }
    await setOpen(false)
    await setFirstTime(false)
  }

  const teamMememberTooltipDesc = (
    <ol className={classes.orderList} data-testid="tooltip-description">
      <li>Filling your company details like company name, origin etc</li>
      <li>
        Connecting your Cloud with Labra by running CFT and creating IAM roles.
        (Your AWS Admin will be able to help with this)
      </li>
      <li>
        Submitting all Listing information which includes your product, pricing,
        buyer registration page and notification details for your listing.
      </li>
    </ol>
  )

  return (
    <div
      data-testid="team-members"
      className={classes.teamMembersContainer}
      id="team-members"
    >
      <div className={classes.teamMembersHeadingContainer}>
        <Typography className={classes.teamMembersHeading}>
          Team members
        </Typography>
        <PopoverHover
          tooltipPlacement="right-start"
          label="Invite members from your team who can assist you with:"
          description={teamMememberTooltipDesc}
          open={open}
          setOpen={setOpen}
          shouldAutoClose={shouldInviteButtonDisabled}
          firstTime={firstTime}
          actionName={shouldInviteButtonDisabled ? undefined : 'Invite now'}
          secondaryActionName={
            shouldInviteButtonDisabled ? undefined : 'Invite later'
          }
          handleAction={shouldInviteButtonDisabled ? undefined : handleAction}
          handleSecondaryAction={inviteLater}
        >
          <div className={classes.infoIconContainer}>
            <FontAwesomeIcon
              icon={faInfoCircle}
              style={{ height: 16, width: 16, display: 'flex' }}
              className={classes.infoIcon}
            />
          </div>
        </PopoverHover>
      </div>
      {shouldInviteButtonDisabled ? (
        <PopoverHover
          tooltipPlacement="right-start"
          label=""
          description={ssoInviteDisableMessage}
          open={openDisabledMessage}
          setOpen={setOpenDisabledMessage}
        >
          <InviteTeamMembersButton isDisabled handleOpen={handleOpen} />
        </PopoverHover>
      ) : (
        <InviteTeamMembersButton isDisabled={false} handleOpen={handleOpen} />
      )}

      <AddNewUserModal openForm={openForm} errors={errors} />
    </div>
  )
}

export interface InviteTeamMembersButtonProps {
  isDisabled: boolean
  handleOpen: () => void
}

export const InviteTeamMembersButton = React.memo(
  ({ isDisabled, handleOpen }: InviteTeamMembersButtonProps) => (
    <ButtonV2
      data-testId="add-new-team-members-button"
      styleType="link"
      smallVariant
      onClick={handleOpen}
      disabled={isDisabled}
      icon={<AddIcon />}
    >
      Invite team members
    </ButtonV2>
  )
)
